import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  CheckCircleIcon,
  Cluster,
  CodeIcon,
  EmptyState,
  Field,
  Input,
  LockIcon,
  Page,
  QueryState,
  RocketIcon,
  Stack,
  StatStrip,
  Toggle,
  WebhookIcon,
} from '@integra/ui';
import {
  useCreateWebhook,
  useDeleteWebhook,
  usePingWebhook,
  useToggleWebhook,
  useWebhooks,
} from '../api/hooks.js';
import { WEBHOOK_EVENTS, type WebhookSubscriptionCreated } from '../api/types.js';

/**
 * Public API + outbound webhooks (§20.5, Platform). Subscribe an https endpoint
 * to platform events; INTEGRA POSTs an HMAC-signed payload when they fire, so a
 * customer wires INTEGRA into their own stack. The signing secret is shown once.
 */
export function WebhooksPage() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();
  const webhooks = useWebhooks(workspaceId);
  const create = useCreateWebhook(workspaceId);
  const toggle = useToggleWebhook(workspaceId);
  const ping = usePingWebhook(workspaceId);
  const remove = useDeleteWebhook(workspaceId);

  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [events, setEvents] = useState<Set<string>>(new Set(['asset.published']));
  const [created, setCreated] = useState<WebhookSubscriptionCreated | null>(null);
  const [copied, setCopied] = useState(false);
  const [pinged, setPinged] = useState<{ id: string; ok: boolean } | null>(null);

  const list = webhooks.data?.data ?? [];

  function toggleEvent(event: string): void {
    setEvents((prev) => {
      const next = new Set(prev);
      if (next.has(event)) next.delete(event);
      else next.add(event);
      return next;
    });
  }

  function submit(): void {
    if (url.trim() === '' || events.size === 0) return;
    create.mutate(
      { url: url.trim(), events: [...events], description: description.trim() || null },
      {
        onSuccess: (res) => {
          setCreated(res.data);
          setCopied(false);
          setUrl('');
          setDescription('');
        },
      },
    );
  }

  function doPing(id: string): void {
    ping.mutate(id, { onSuccess: (res) => setPinged({ id, ok: res.delivered }) });
  }

  return (
    <Page
      title="API & webhooks"
      icon={WebhookIcon}
      description="Wire INTEGRA into your stack. Subscribe an endpoint to platform events and receive HMAC-signed payloads, or drive the platform with a scoped API key."
      stats={
        list.length > 0 ? (
          <StatStrip
            stats={[
              { icon: WebhookIcon, label: 'Endpoints', value: list.length },
              {
                icon: CheckCircleIcon,
                label: 'Active',
                value: list.filter((s) => s.active).length,
                tone: 'accent',
              },
            ]}
          />
        ) : undefined
      }
    >
      <Stack gap="lg">
        <Alert tone="info" title="Verifying deliveries">
          Every delivery is signed with your secret as{' '}
          <span className="font-mono text-xs">X-Integra-Signature: sha256=…</span> over the raw
          body. Recompute the HMAC-SHA256 to verify a payload is genuinely from INTEGRA. Scoped API
          keys for the REST API are managed per workspace.
        </Alert>

        <Card title="Add a webhook" subtitle="An https endpoint that receives event payloads.">
          <Stack gap="md">
            <Field label="Endpoint URL" htmlFor="wh-url">
              <Input
                id="wh-url"
                value={url}
                placeholder="https://example.com/hooks/integra"
                onChange={(e) => setUrl(e.target.value)}
              />
            </Field>
            <Field label="Description (optional)" htmlFor="wh-desc">
              <Input
                id="wh-desc"
                value={description}
                placeholder="Our data pipeline"
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Events
              </p>
              <Stack gap="sm">
                {WEBHOOK_EVENTS.map((event) => (
                  <Cluster key={event} justify="between" align="center">
                    <span className="font-mono text-sm text-navy-700">{event}</span>
                    <Toggle
                      label={event}
                      checked={events.has(event)}
                      onChange={() => toggleEvent(event)}
                    />
                  </Cluster>
                ))}
              </Stack>
            </div>
            <Cluster justify="end">
              <Button
                loading={create.isPending}
                disabled={url.trim() === '' || events.size === 0}
                onClick={submit}
              >
                Add webhook
              </Button>
            </Cluster>

            {created !== null && (
              <Alert
                tone="warning"
                title="Copy the signing secret now — it will not be shown again"
              >
                <Cluster gap="sm" align="center">
                  <span className="break-all font-mono text-xs text-navy-700">
                    {created.secret}
                  </span>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      void navigator.clipboard?.writeText(created.secret);
                      setCopied(true);
                    }}
                  >
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </Cluster>
              </Alert>
            )}
            {create.error instanceof Error && (
              <Alert tone="error" title="Could not add the webhook">
                {create.error.message}
              </Alert>
            )}
          </Stack>
        </Card>

        <QueryState loading={webhooks.isLoading} error={webhooks.error} what="webhooks">
          {list.length === 0 ? (
            <EmptyState
              icon={WebhookIcon}
              title="No webhooks yet"
              description="Add one above to notify your systems when events happen in this workspace."
              features={[
                { icon: CodeIcon, label: 'Subscribe an https endpoint to events' },
                { icon: LockIcon, label: 'HMAC-signed payloads you can verify' },
                { icon: RocketIcon, label: 'Wire INTEGRA into your own stack' },
              ]}
            />
          ) : (
            <Stack gap="md">
              {list.map((sub) => (
                <Card key={sub.id}>
                  <Stack gap="sm">
                    <Cluster justify="between" align="start" gap="md">
                      <div className="min-w-0">
                        <p className="truncate font-mono text-sm text-navy-700">{sub.url}</p>
                        <Cluster gap="sm" align="center" className="mt-1">
                          {sub.events.map((e) => (
                            <Badge key={e} tone="neutral">
                              {e}
                            </Badge>
                          ))}
                          <Badge tone={sub.active ? 'success' : 'warning'} dot>
                            {sub.active ? 'Active' : 'Paused'}
                          </Badge>
                        </Cluster>
                      </div>
                      <Cluster gap="sm" align="center">
                        <Toggle
                          label="Active"
                          checked={sub.active}
                          onChange={(next) => toggle.mutate({ id: sub.id, active: next })}
                        />
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={ping.isPending && ping.variables === sub.id}
                          onClick={() => doPing(sub.id)}
                        >
                          Send test
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={remove.isPending && remove.variables === sub.id}
                          onClick={() => remove.mutate(sub.id)}
                        >
                          Delete
                        </Button>
                      </Cluster>
                    </Cluster>
                    {pinged !== null && pinged.id === sub.id && (
                      <p
                        className={
                          pinged.ok ? 'text-xs text-state-success' : 'text-xs text-state-error'
                        }
                      >
                        {pinged.ok
                          ? 'Test delivered — your endpoint returned 2xx.'
                          : 'Test failed — your endpoint did not accept the delivery.'}
                      </p>
                    )}
                    {sub.lastDeliveryAt !== null && (
                      <p className="text-[11px] text-ink-muted">
                        Last delivery {new Date(sub.lastDeliveryAt).toLocaleString()}
                      </p>
                    )}
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
        </QueryState>
      </Stack>
    </Page>
  );
}
